import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/** A single "Variabel rekeningnummer" row for a general ledger account. */
export interface VariableElement {
  /** Id used for update/delete. Empty for a new (unsaved) row. */
  variableElementId: string;
  generalLedgerAccountId: string;
  /** Selected "Gegevenselement". Empty when the row only carries a fixed value. */
  elementId: string;
  /** "Beginpositie" (nullable). */
  startPosition: number | null;
  /** "Lengte" (nullable). */
  numberOfPositions: number | null;
  /** "Vaste waarde" (empty string is stored as null by the backend). */
  fixedValue: string;
}

/** A selectable "Gegevenselement" option. */
export interface ElementOption {
  id: string;
  label: string;
}

interface VariableElementApiModel {
  generalLedgerAccount_VariableElement_ID: string;
  generalLedgerAccount_ID: string;
  element_ID: string | null;
  startPosition: number | null;
  numberOf_Positions: number | null;
  fixed_Value: string | null;
}

interface VariableElementApiPayload {
  generalLedgerAccount_VariableElement_ID: string;
  generalLedgerAccount_ID: string;
  element_ID: string;
  startPosition: number | null;
  numberOf_Positions: number | null;
  fixed_Value: string;
}

interface ElementApiModel {
  element_ID: string;
  rubric?: string | null;
  element_Name: string;
}

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * CRUD access to a general ledger account's "Variabel rekeningnummer" rows,
 * backed by /kbj-frontend/api/v1/GeneralLedgerAccount/GeneralLedgerAccountVariableElement,
 * plus the selectable data elements from /kbj-frontend/api/v1/Element/All.
 */
@Injectable({
  providedIn: 'root',
})
export class GeneralLedgerAccountVariableElementService {
  private apiUrl =
    environment.apiUrl +
    '/kbj-frontend/api/v1/GeneralLedgerAccount/GeneralLedgerAccountVariableElement';
  private elementUrl = environment.apiUrl + '/kbj-frontend/api/v1/Element';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns the selectable "Gegevenselement" options. Empty on 204/404.
   */
  getElementOptions(): Observable<ElementOption[]> {
    return this.http
      .get<ElementApiModel[] | null>(this.elementUrl + '/All', this.httpOptions)
      .pipe(
        map((data) =>
          (data ?? []).map((element) => ({
            id: element.element_ID,
            label: element.rubric
              ? `${element.rubric} - ${element.element_Name}`
              : element.element_Name,
          })),
        ),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of<ElementOption[]>([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Returns the variable elements configured for the given account.
   * Returns an empty array when the API responds with 204 No Content or 404.
   */
  getByAccount(generalLedgerAccountId: string): Observable<VariableElement[]> {
    return this.http
      .get<VariableElementApiModel[] | null>(
        this.apiUrl + '/ByContext/' + generalLedgerAccountId,
        this.httpOptions,
      )
      .pipe(
        map((data) => (data ?? []).map((item) => this.mapToModel(item))),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 404 || err.status === 204) {
            return of<VariableElement[]>([]);
          }
          return this.handleError(err);
        }),
      );
  }

  /**
   * Creates a new variable element. The id is forced to the empty guid so the
   * backend generates a new one.
   */
  add(element: VariableElement): Observable<unknown> {
    const payload = this.mapToPayload({ ...element, variableElementId: EMPTY_GUID });
    return this.http
      .post<unknown>(this.apiUrl, payload, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /** Updates an existing variable element. */
  update(id: string, element: VariableElement): Observable<unknown> {
    const payload = this.mapToPayload({ ...element, variableElementId: id });
    return this.http
      .put<unknown>(this.apiUrl + '/' + id, payload, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /** Removes a variable element by its id. */
  remove(id: string): Observable<unknown> {
    return this.http
      .delete(this.apiUrl + '/' + id, this.httpOptions)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  private mapToModel(api: VariableElementApiModel): VariableElement {
    return {
      variableElementId: api.generalLedgerAccount_VariableElement_ID,
      generalLedgerAccountId: api.generalLedgerAccount_ID,
      elementId: api.element_ID ?? '',
      startPosition: api.startPosition ?? null,
      numberOfPositions: api.numberOf_Positions ?? null,
      fixedValue: api.fixed_Value ?? '',
    };
  }

  private mapToPayload(model: VariableElement): VariableElementApiPayload {
    return {
      generalLedgerAccount_VariableElement_ID: model.variableElementId || EMPTY_GUID,
      generalLedgerAccount_ID: model.generalLedgerAccountId,
      element_ID: model.elementId || EMPTY_GUID,
      startPosition: model.startPosition,
      numberOf_Positions: model.numberOfPositions,
      // An empty string is stored as null by the backend.
      fixed_Value: model.fixedValue ?? '',
    };
  }

  private handleError(err: HttpErrorResponse) {
    const errorMessage = `An error has occurred: ${err.error?.message ?? err.message}`;
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}
